/**
 * Native Android speech bridge for AgentChat dictation.
 *
 * The Capacitor APK exposes Android's SpeechRecognizer through the
 * AgentChatSpeech plugin. Browser/PWA builds use the web stub so callers can
 * feature-detect cleanly without touching Capacitor internals.
 */

import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core'

export type NativeSpeechState = 'ready' | 'listening' | 'speech' | 'processing' | 'stopped'

export type NativeSpeechResultEvent = {
  transcript: string
  alternatives?: string[]
  confidence?: number
}

export type NativeSpeechErrorEvent = {
  code: string
  message: string
  recoverable?: boolean
}

export type NativeSpeechStateEvent = {
  state: NativeSpeechState
}

export interface AgentChatSpeechPlugin {
  isAvailable(): Promise<{
    available: boolean
    permission: 'granted' | 'denied' | 'prompt'
  }>
  start(options?: {
    locale?: string
    partialResults?: boolean
    continuous?: boolean
  }): Promise<{ ok: boolean }>
  stop(): Promise<{ ok: boolean }>
  cancel(): Promise<{ ok: boolean }>
  requestPermission(): Promise<{ state: 'granted' | 'denied' | 'prompt' }>
  addListener(
    eventName: 'partialResult',
    listenerFunc: (event: NativeSpeechResultEvent) => void,
  ): Promise<PluginListenerHandle>
  addListener(
    eventName: 'finalResult',
    listenerFunc: (event: NativeSpeechResultEvent) => void,
  ): Promise<PluginListenerHandle>
  addListener(
    eventName: 'error',
    listenerFunc: (event: NativeSpeechErrorEvent) => void,
  ): Promise<PluginListenerHandle>
  addListener(
    eventName: 'stateChange',
    listenerFunc: (event: NativeSpeechStateEvent) => void,
  ): Promise<PluginListenerHandle>
}

const unavailable = async () => {
  throw new Error('native Android speech is unavailable in this build')
}

const AgentChatSpeech = registerPlugin<AgentChatSpeechPlugin>('AgentChatSpeech', {
  web: () => Promise.resolve({
    isAvailable: async () => ({ available: false, permission: 'denied' as const }),
    start: unavailable,
    stop: unavailable,
    cancel: unavailable,
    requestPermission: async () => ({ state: 'denied' as const }),
    addListener: async () => ({ remove: async () => undefined }),
  }),
})

export function isNativeSpeechAvailable(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('AgentChatSpeech')
}

export { AgentChatSpeech }
